import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { VictoryPie } from 'victory';
import download from '../../../../../../assets/images/icons/download.svg';
import info from '../../../../../../assets/images/icons/info.svg';
import NoData from '../../../../../../components/NoData';
import OpacityAnimation from '../../../../../../components/OpacityAnimation';
import useThemeContext from '../../../../../../contexts/theme';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import ConsultsService from '../../../../../../services/ConsultsService';
import ExportXlsx from '../../../../../../utils/ExportXlsx';
import { PeriodType } from '../../../types';
import { IReportApiResponse } from './DownloadOtimizationModal/types';
import { Card } from './styles';

interface StatusChartCardI {
  totalConsults: number;
  totalReclamations: number;
  period: PeriodType;
}

export default function TotalReclamationsCard({ totalConsults, totalReclamations, period }: StatusChartCardI) {
  const { selectedTheme } = useThemeContext();
  const { permissions } = useGetGeneralAppData();
  const reclamationsPermissions = permissions.find((x) => x.codPag === 8);
  const [labelBeingShow, setLabelBeingShow] = useState({ description: '', value: '' });
  const navigate = useNavigate();

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode, companyName } = useGetGeneralAppData();

  // const currentDate = new Date();
  // const lastDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  // const firstDayOfCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);

  const lastDayOfPeriodMonth = new Date(period.year, period.month, 0);
  const firstDayOfPeriodMonth = new Date(period.year, period.month - 1, 1);

  async function downloadRel() {
    await toast.promise(apiCall({
      apiToCall: ConsultsService.getGeneralReports,
      queryParams: {
        codUsu: userCode,
        perfilUsu: userProfile,
        codEmpresa: companyCode,
        data1: firstDayOfPeriodMonth.toISOString().split('T')[0],
        data2: lastDayOfPeriodMonth.toISOString().split('T')[0],
        valorInt: 0,
        valorInt1: 0,
        valorInt2: 0,
        valorLong: 0,
        tipoRel: 571,
      },
      actionAfterResponse: (apiResponse: IReportApiResponse) => {
        if (!apiResponse.codigo) {
          toast.error(`Não foi possível baixar a planilha (${apiResponse.msg})`);
          return;
        }
        if (apiResponse.rel57.length === 0) {
          toast.error(`Não há dados no período selecionado (${apiResponse.msg})`);
          return;
        }

        const report = apiResponse.rel57 as unknown as { [key: string]: string | number }[];
        const mappedReport = report.map((item) => {
          if (String(item.Data_da_Solução).includes('1900')) {
            item.Data_da_Solução = 'Sem Data';
          }
          return item;
        });

        ExportXlsx({
          filename: `Relatório contestações - Emp_${companyCode}_${companyName} ${period.month}_${period.year}`,
          data: mappedReport,
        });
      },
      catchMessage: 'Não foi possível baixar o relatório',
    }), {
      pending: 'Sua planilha está sendo baixada',
      success: 'Sua planilha foi baixada!',
      error: 'Não foi possível baixar sua planilha',
    });
  }

  return (
    <Card>
      <div className="card-title">
        <div>
          Contestações ({totalReclamations} / {totalConsults})
          <img src={info} alt="" className='primaryColor info' title='Informa a quantidade de contestações realizadas no mês selecionado'/>
        </div>
        <img src={download} alt="download" title="Baixar relatório excel" onClick={() => downloadRel()} />
      </div>

      {(!!totalConsults && !!totalReclamations) && (
        <>
          <OpacityAnimation>
            <div className="card-main-graph">
              <VictoryPie
                data={[
                  { x: 'Sem contestação', y: totalConsults - totalReclamations, label: `${totalConsults - totalReclamations} (${((totalConsults - totalReclamations) / totalConsults * 100).toFixed(1)}%)\nSem contestação` },
                  { x: 'Com contestação', y: totalReclamations, label: `${totalReclamations} (${(totalReclamations / totalConsults * 100).toFixed(1)}%)\nCom contestação` }
                ]}
                animate={{
                  duration: 500
                }}
                colorScale="qualitative"
                height={335}
                labelPlacement="vertical"
                labelRadius={0.1}
                labelPosition="endAngle"
                style={{
                  labels: { fontSize: 0, fill: selectedTheme === 'dark' ? '#fff' : '#000' }
                }}
                padding={{ top: 0, bottom: 10 }}
                innerRadius={110}
                padAngle={1}
                events={[{
                  target: 'data',
                  eventHandlers: {
                    onMouseOver: () => {
                      return [
                        {
                          target: 'data',
                          mutation: ({ style, data, index }) => {
                            const percentage = ((data[index].y / totalConsults) * 100).toFixed(1);
                            setLabelBeingShow({ description: `${data[index].x} (${data[index].y})`, value: `${percentage}%` });
                            return { style: Object.assign({}, style, { opacity: '0.8', cursor: data[index].x === 'Com contestação' ? 'pointer' : 'help' }) };
                          }
                        }
                      ];
                    },
                    onMouseOut: () => {
                      return [
                        {
                          target: 'data',
                          mutation: () => {
                            setLabelBeingShow({ description: '', value: '' });
                            return null;
                          }
                        }
                      ];
                    },
                    onClick: () => {
                      if (!reclamationsPermissions?.vizualizar) {
                        return;
                      }
                      return [
                        {
                          target: 'data',
                          mutation: ({ data, index }) => {
                            if (data[index].x === 'Com contestação') {
                              navigate(`/periodReclamations?month=${period.month}&year=${period.year}&active=Home&returnLink=${encodeURIComponent('/')}`);
                            }
                          }
                        }
                      ];
                    }
                  }
                }]}
              />
              <label>
                <span>{labelBeingShow.value || ''}</span>
                <span>{labelBeingShow.description}</span>
              </label>
            </div>
          </OpacityAnimation>
        </>
      )}

      {(!totalReclamations) && (
        <NoData
          icon="emptyBox"
          label={(
            <>
              Não foi realizada nenhuma contestação nas consultas do período selecionado
            </>
          )}
        />
      )}
    </Card>
  );
}
